import * as THREE from "three";
import { defineModel, styleV3, type Part } from "../../lib";

// A WWI tethered kite balloon: an elongated khaki gas envelope floating high
// over its ground winch, with tail fins, a tiny wicker observer basket slung
// on cables, and a taut tether running down to the winch drum at y = 0.
// Everything airborne is named "balloon" so the "sway" animation can bob the
// whole aerostat gently while the winch stays put. Nose points +z.

const { box, cyl } = styleV3;

const KHAKI = "#7a6f4e";
const OLIVE = "#5f6b3c";
const GUNMETAL = "#4a4d52";
const CANVAS = "#cbbd93";
const TYRE = "#33342f";

const ENV_Y = 1.55; // envelope centre height
const BASKET_Y = 1.0; // basket centre height
const WINCH: [number, number, number] = [0.18, 0, -0.18];

// A thin cylinder strut/cable from point a to point b.
function cable(
  a: [number, number, number],
  b: [number, number, number],
  r: number,
  color: string,
  name?: string,
): Part {
  const av = new THREE.Vector3(...a);
  const bv = new THREE.Vector3(...b);
  const dir = bv.clone().sub(av);
  const len = dir.length();
  const mid = av.clone().add(bv).multiplyScalar(0.5);
  const q = new THREE.Quaternion().setFromUnitVectors(
    new THREE.Vector3(0, 1, 0),
    dir.normalize(),
  );
  const e = new THREE.Euler().setFromQuaternion(q, "XYZ");
  return {
    geometry: new THREE.CylinderGeometry(r, r, len, 4),
    color,
    position: [mid.x, mid.y, mid.z],
    rotation: [e.x, e.y, e.z],
    name,
  };
}

function parts(): Part[] {
  const out: Part[] = [];

  // -- Gas envelope: a stretched sphere plus a belly cylinder (capsule read) --
  out.push({
    geometry: new THREE.SphereGeometry(0.26, 10, 8),
    color: KHAKI,
    position: [0, ENV_Y, 0.02],
    scale: [0.82, 0.82, 1.65],
    name: "balloon",
  });
  out.push({
    geometry: new THREE.CylinderGeometry(0.185, 0.185, 0.5, 10),
    color: KHAKI,
    position: [0, ENV_Y - 0.02, -0.02],
    rotation: [Math.PI / 2, 0, 0],
    name: "balloon",
  });

  // -- Tail fins at the rear (-z): one upper fin, two side fins ---------------
  out.push({
    geometry: new THREE.BoxGeometry(0.03, 0.2, 0.18),
    color: OLIVE,
    position: [0, ENV_Y + 0.14, -0.36],
    rotation: [0.35, 0, 0],
    name: "balloon",
  });
  for (const sx of [-1, 1]) {
    out.push({
      geometry: new THREE.BoxGeometry(0.2, 0.03, 0.18),
      color: OLIVE,
      position: [sx * 0.17, ENV_Y - 0.02, -0.36],
      rotation: [0, sx * 0.3, 0],
      name: "balloon",
    });
  }

  // -- Wicker observer basket slung under the belly ---------------------------
  out.push({
    geometry: new THREE.BoxGeometry(0.14, 0.11, 0.14),
    color: CANVAS,
    position: [0, BASKET_Y, 0],
    name: "balloon",
  });
  out.push({
    geometry: new THREE.BoxGeometry(0.155, 0.02, 0.155),
    color: OLIVE,
    position: [0, BASKET_Y + 0.055, 0],
    name: "balloon",
  });

  // Four suspension cables from the envelope belly to the basket corners.
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      out.push(
        cable(
          [sx * 0.06, BASKET_Y + 0.05, sz * 0.06],
          [sx * 0.13, ENV_Y - 0.16, sz * 0.16],
          0.005,
          TYRE,
          "balloon",
        ),
      );
    }
  }

  // -- Tether down to the winch (bottom sunk into the drum so a small bob
  //    never detaches it) ------------------------------------------------------
  out.push(
    cable([WINCH[0], 0.1, WINCH[2]], [0, BASKET_Y - 0.055, 0], 0.007, TYRE, "balloon"),
  );

  // -- Ground winch: anchor block, cable drum, crank ---------------------------
  out.push(box(0.24, 0.12, 0.2, GUNMETAL, [WINCH[0], 0.06, WINCH[2]]));
  out.push(cyl(0.05, 0.05, 0.16, 8, TYRE, [WINCH[0], 0.14, WINCH[2]], [0, 0, Math.PI / 2]));
  out.push(cyl(0.012, 0.012, 0.09, 5, GUNMETAL, [WINCH[0] + 0.1, 0.17, WINCH[2]], [0, 0, 0.6]));

  return out;
}

export const observationBalloon = defineModel({
  id: "observation-balloon",
  name: "Observation Balloon",
  batchId: "wwi",
  tags: ["vehicle", "wwi", "military", "balloon", "aircraft", "recon", "animated"],
  summary: "A tethered khaki kite balloon with an observer basket and ground winch.",
  description:
    "A WWI observation balloon: an elongated khaki gas envelope with tail fins " +
    "floating above its tile, a wicker observer basket slung on cables, and a " +
    "taut tether running down to the winch anchored at ground level. The whole " +
    "aerostat bobs gently on the looping \"sway\" animation.",
  author: "Fable 5",
  footprint: 0.5,
  tiles: { w: 1, h: 1 },
  parts,
  animations: [
    {
      id: "sway",
      label: "Swaying on the tether",
      duration: 6,
      loop: true,
      tracks: [
        {
          target: "balloon",
          pivot: [0, ENV_Y, 0],
          position: [
            { t: 0, value: [0, 0, 0] },
            { t: 1.5, value: [0.02, 0.03, 0.01] },
            { t: 3, value: [0, 0.05, 0.02] },
            { t: 4.5, value: [-0.02, 0.03, 0] },
            { t: 6, value: [0, 0, 0] },
          ],
        },
      ],
    },
  ],
});
